const DEV_TOKEN_KEY = "vetcan.devToken";
const OPERATOR_TOKEN_KEY = "vetcan.operatorToken";
const JWT_TRAILING_UNSAFE_CHARS = /[^A-Za-z0-9_-]$/;

const stripEnclosingQuotes = (value: string) => {
  const trimmed = value.trim();
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
};

const stripEnclosingAngles = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.startsWith("<") && trimmed.endsWith(">")) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
};

const stripTrailingUnsafeJwtChars = (value: string) =>
  value.replace(JWT_TRAILING_UNSAFE_CHARS, "").trim();
const stripLeadingWrapperChars = (value: string) =>
  value.replace(/^[<"']+/, "").trim();

function sanitizeWrappedInput(
  input: unknown,
  options?: { stripBearer?: boolean }
) {
  let value = typeof input === "string" ? input.trim() : "";
  if (!value) return "";

  const stripBearer = options?.stripBearer ?? false;
  for (let i = 0; i < 8; i += 1) {
    const prior = value;
    value = stripEnclosingQuotes(value);
    value = stripEnclosingAngles(value);
    value = stripLeadingWrapperChars(value);
    if (stripBearer) {
      value = value.replace(/^bearer\s+/i, "").trim();
    }
    value = stripTrailingUnsafeJwtChars(value);
    if (value === prior) break;
  }

  return value.trim();
}

export function normalizeTokenInput(input: unknown): string {
  return sanitizeWrappedInput(input, { stripBearer: true });
}

function parseSearchParams(input: string | undefined) {
  const raw = typeof input === "string" ? input.trim() : "";
  if (!raw) return new URLSearchParams();
  const normalized = raw.startsWith("?") ? raw.slice(1) : raw;
  return new URLSearchParams(normalized);
}

function parseHashParams(input: string | undefined) {
  const rawHash = typeof input === "string" ? input.trim() : "";
  if (!rawHash) return new URLSearchParams();

  const normalizedHash = rawHash.startsWith("#")
    ? rawHash.slice(1)
    : rawHash;
  if (!normalizedHash) return new URLSearchParams();

  if (normalizedHash.includes("?")) {
    const query = normalizedHash.slice(normalizedHash.indexOf("?") + 1);
    return new URLSearchParams(query);
  }

  return new URLSearchParams(normalizedHash);
}

function getLocationParamValue(
  search: string | undefined,
  hash: string | undefined,
  key: string
) {
  const searchParams = parseSearchParams(search);
  const fromSearch = searchParams.get(key);
  if (typeof fromSearch === "string" && fromSearch.trim()) {
    return fromSearch;
  }

  const hashParams = parseHashParams(hash);
  const fromHash = hashParams.get(key);
  if (typeof fromHash === "string" && fromHash.trim()) {
    return fromHash;
  }

  return null;
}

export function getMetricsTokenFromLocation(
  search: string | undefined,
  hash: string | undefined
) {
  const rawToken = getLocationParamValue(search, hash, "token");
  const token = normalizeTokenInput(rawToken);
  return token || null;
}

export function getMetricsCodeFromLocation(
  search: string | undefined,
  hash: string | undefined
) {
  const rawCode = getLocationParamValue(search, hash, "code");
  const normalized = sanitizeWrappedInput(rawCode);
  return normalized || null;
}

export function getMetricsBypassKeyFromLocation(
  search: string | undefined,
  hash: string | undefined
) {
  const rawBypassKey = getLocationParamValue(search, hash, "k");
  const normalized = sanitizeWrappedInput(rawBypassKey);
  return normalized || null;
}

export type MetricsTokenSource = "code" | "link" | "storage" | "none";

export function resolveMetricsTokenPreference(input: {
  search?: string;
  hash?: string;
  storedToken?: string | null;
}) {
  const linkToken = getMetricsTokenFromLocation(input.search, input.hash);
  if (linkToken) {
    return {
      token: linkToken,
      source: "link" as const,
    };
  }

  const storedToken = normalizeTokenInput(input.storedToken);
  if (storedToken) {
    return {
      token: storedToken,
      source: "storage" as const,
    };
  }

  return {
    token: null,
    source: "none" as const,
  };
}

export function getOperatorToken(): string | null {
  const operatorToken = normalizeTokenInput(
    localStorage.getItem(OPERATOR_TOKEN_KEY)
  );
  return operatorToken || null;
}

export function getAuthToken(): string | null {
  const operatorToken = getOperatorToken();
  if (operatorToken) return operatorToken;

  const devToken = normalizeTokenInput(localStorage.getItem(DEV_TOKEN_KEY));
  return devToken || null;
}

export function setAuthToken(token: string) {
  const key = import.meta.env.DEV ? DEV_TOKEN_KEY : OPERATOR_TOKEN_KEY;
  const normalized = normalizeTokenInput(token);
  if (!normalized) {
    localStorage.removeItem(key);
    return;
  }
  localStorage.setItem(key, normalized);
}

export function clearAuthToken() {
  localStorage.removeItem(DEV_TOKEN_KEY);
  localStorage.removeItem(OPERATOR_TOKEN_KEY);
}

export function setOperatorToken(token: string) {
  const value = normalizeTokenInput(token);
  if (!value) {
    localStorage.removeItem(OPERATOR_TOKEN_KEY);
    return;
  }
  localStorage.setItem(OPERATOR_TOKEN_KEY, value);
}

export function clearOperatorToken() {
  localStorage.removeItem(OPERATOR_TOKEN_KEY);
}

type JwtPayload = {
  sub?: string;
  role?: string;
  scope?: string | string[];
  typ?: string;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
};

type JwtHeader = {
  alg?: string;
  typ?: string;
  [key: string]: unknown;
};

type DecodedJwtToken = {
  header: JwtHeader;
  payload: JwtPayload;
};

const decodeBase64Url = (value: string): string | null => {
  try {
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padding = (4 - (normalized.length % 4)) % 4;
    const padded = normalized + "=".repeat(padding);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
};

function parseJwtPart(value: string): Record<string, unknown> | null {
  const decoded = decodeBase64Url(value);
  if (!decoded) return null;
  try {
    const parsed = JSON.parse(decoded) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function decodeJwtPayload(token: string): DecodedJwtToken | null {
  const value = normalizeTokenInput(token);
  if (!value) return null;

  const parts = value.split(".");
  if (parts.length < 2) return null;

  const header = parseJwtPart(parts[0]);
  const payload = parseJwtPart(parts[1]);
  if (!header || !payload) return null;

  return {
    header: header as JwtHeader,
    payload: payload as JwtPayload,
  };
}

export function jwtNumericDateToIso(value: unknown): string | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const date = new Date(value * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export async function initDevAuth() {
  // DEV only: auto-mint a token for local/ngrok workflows
  if (!import.meta.env.DEV) return;

  const existing = getAuthToken();
  if (existing && existing.trim()) return;

  try {
    const res = await fetch("/api/auth/dev-token", { method: "POST" });
    if (!res.ok) return;
    const data: { token?: string } = await res.json();
    if (data.token) {
      setAuthToken(data.token);
    }
  } catch {
    // silent
  }
}
